import React, { useRef, useState } from "react";
import MtiButton from "../Buttons/MtiButton";
import IconApp from "../../Icons/AppIcon";
import "./LicenseWizard.css";
import RadioButton from "../Buttons/RadioButton";
import TextBox from "../Common/TextBox";
import LicenseApi from "../../api/LicenseApi";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { sendNotification } from "../../redux/actions/NotificationAction";
import { ENotificationType } from "../../Model/ENotificationType";
import LicenseRequestModal from "./LicenseRequestModal";

type LicenseHomeProps = {
  onActivateButton: () => void;
  onRequestButton: () => void;
};

type LicenseActivationProps = {
  onBack?: () => void;
  onClose?: () => void;
  onStartActivation?: () => void;
  onStopActivation?: () => void;
  onTriggerManualErrorNotif?: () => void;
};

type WizardProps = {
  setShowLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setShowErrorNotification: React.Dispatch<React.SetStateAction<boolean>>;
};

export enum ELicenseViewState {
  Home,
  Request,
  Activation,
}

export enum ELicenseActionType {
  Manual,
  Online,
}

export function LicenseHome({
  onActivateButton,
  onRequestButton,
}: LicenseHomeProps) {
  return (
    <>
      <div data-testid="LH" className="login-title">
        <div className="title-container">
          <IconApp width="34" height="34" viewBox="0 0 48 48" fill="none" />
          <p>
            ROCK IMAGER<sup>®</sup>
          </p>
        </div>
      </div>
      <div className="license-content">
        <div className="license-header">License Not Activated</div>
        <div className="license-body">
          <p>
            You must activate the license in order to access ROCK IMAGER
            software.
          </p>
        </div>
        <div className="license-footer">
          <MtiButton
            size="large"
            style={{ width: 131, marginRight: 15 }}
            onClick={onActivateButton}
          >
            ACTIVATE
          </MtiButton>
          <MtiButton
            size="large"
            style={{ width: 218 }}
            onClick={onRequestButton}
          >
            REQUEST LICENSE
          </MtiButton>
        </div>
      </div>
    </>
  );
}

export function LicenseActivation({
  onBack,
  onClose,
  onStartActivation = () => {},
  onStopActivation = () => {},
  onTriggerManualErrorNotif = () => {},
}: Readonly<LicenseActivationProps>) {
  const [activationType, setactivationType] = useState(
    ELicenseActionType.Online
  );
  const [clientId, setClientId] = useState("");
  const licenseFileInputRef = useRef<HTMLInputElement>(null);
  const history = useHistory();
  const dispatch = useDispatch();

  const { pathname } = useLocation();

  const isReportSupportPage = pathname.includes("/report-support");

  const activateLicense = async () => {
    switch (activationType) {
      case ELicenseActionType.Online:
        try {
          onStartActivation();
          await LicenseApi.activateOnline(clientId);
          onStopActivation();
          if (isReportSupportPage) {
            dispatch(
              sendNotification(
                ENotificationType.Success,
                "Your Changes have been saved successfully"
              )
            );
          }
          onClose?.();
        } catch (error) {
          onStopActivation();
          onTriggerManualErrorNotif();
        }
        break;
      case ELicenseActionType.Manual:
        licenseFileInputRef.current?.click();
        break;
    }
  };

  const onChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    onStartActivation();
    const file = event.target.files ? event.target.files[0] : null;
    if (file !== null) {
      const reader = new FileReader();
      reader.onload = function (event: ProgressEvent<FileReader>) {
        LicenseApi.activateManual(event.target?.result as string)
          .then(() => {
            LicenseApi.getLicenseInfo().then((value) => {
              onStopActivation();
              if (value.isAllowToRun) {
                history.push("/");
                window.location.reload();
              } else {
                dispatch(
                  sendNotification(
                    ENotificationType.Error,
                    "License Activation Failed, Please check the License File or Contact Formulatrix Support."
                  )
                );
                onTriggerManualErrorNotif();
              }
            });
          })
          .catch(() => {
            onStopActivation();
          });
      };

      reader.readAsText(file);
    }
  };

  const NOP = () => {};

  return (
    <>
      <div data-testid="LA" className="login-title">
        <div className="title-container">
          <IconApp width="34" height="34" viewBox="0 0 48 48" fill="none" />
          <p>
            ROCK IMAGER<sup>®</sup>
          </p>
        </div>
      </div>
      <div className="license-content">
        <div className="license-header">License Activation</div>
        <div className="license-body">
          <div>Please select the license activation method:</div>
          <RadioButton
            id="activation-license-online"
            name="activation-license-online"
            label="Online Activation"
            checked={activationType === ELicenseActionType.Online}
            onChange={() => setactivationType(ELicenseActionType.Online)}
            style={{ marginTop: 12 }}
          />
          {activationType === ELicenseActionType.Online && (
            <>
              <p style={{ paddingLeft: 35, marginTop: 12, marginBottom: 0 }}>
                Input the client ID that you have received via email and tap
                ACTIVATE to activate your license. If you have not received an
                email with your client ID, please contact{" "}
                <strong>support@formulatrix.com</strong> for assistance.
              </p>
              <div style={{ display: "flex", paddingLeft: 35, marginTop: 7 }}>
                <span
                  style={{
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginLeft: 0,
                  }}
                >
                  Client ID{" "}
                </span>
                <TextBox
                  name="clientId"
                  value={clientId}
                  onChange={(value) => setClientId(value as string)}
                  style={{
                    marginLeft: 10,
                    width: 297,
                    height: 47,
                    border: "1px solid #808080",
                    boxSizing: "border-box",
                    borderRadius: "5px",
                  }}
                />
              </div>
            </>
          )}
          <RadioButton
            id="activation-license-manual"
            name="activation-license-manual"
            label="Manual Activation"
            checked={activationType === ELicenseActionType.Manual}
            onChange={() => setactivationType(ELicenseActionType.Manual)}
            style={{ marginTop: 12 }}
          />
          {activationType === ELicenseActionType.Manual && (
            <p style={{ paddingLeft: 35, marginTop: 12 }}>
              Select ACTIVATE button and choose an activation file (.xml) from
              your local drive or external file. NOTE: If you have trouble
              activating the license, please contact our support at{" "}
              <strong>support@formulatrix.com</strong> for assistance.
            </p>
          )}
        </div>
        <div className="license-footer">
          <input
            ref={licenseFileInputRef}
            type="file"
            name="file"
            onChange={onChangeFile}
            accept=".xml"
          />
          <MtiButton
            size="large"
            style={{ width: 150, marginRight: 15 }}
            onClick={activateLicense}
          >
            ACTIVATE
          </MtiButton>
          <MtiButton
            size="large"
            style={{ width: 150 }}
            variant="secondary"
            onClick={onBack ?? onClose ?? NOP}
          >
            {onBack ? "BACK" : "CLOSE"}
          </MtiButton>
        </div>
      </div>
    </>
  );
}

export default function LicenseWizard({
  setShowLoading,
  setShowErrorNotification,
}: WizardProps) {
  const [licenseView, setLicenseView] = useState(ELicenseViewState.Home);

  return (
    <div className="license-wizard">
      {licenseView === ELicenseViewState.Home && (
        <LicenseHome
          onActivateButton={() => setLicenseView(ELicenseViewState.Activation)}
          onRequestButton={() => setLicenseView(ELicenseViewState.Request)}
        />
      )}
      {licenseView === ELicenseViewState.Request && (
        <LicenseRequestModal
          onBack={() => setLicenseView(ELicenseViewState.Home)}
          onStartRequest={() => setShowLoading(true)}
          onStopRequest={() => setShowLoading(false)}
        />
      )}
      {licenseView === ELicenseViewState.Activation && (
        <LicenseActivation
          onBack={() => setLicenseView(ELicenseViewState.Home)}
          onStartActivation={() => setShowLoading(true)}
          onStopActivation={() => setShowLoading(false)}
          onTriggerManualErrorNotif={() => setShowErrorNotification(true)}
        />
      )}
    </div>
  );
}
